/*
 *  TTTech nerve-management-system-frontend
 *  Copyright(c) 2023. TTTech Industrial Automation AG.
 *
 *  ALL RIGHTS RESERVED.
 *
 *  Usage of this software, including source code, netlists, documentation,
 *  is subject to restrictions and conditions of the applicable license
 *  agreement with TTTech Industrial Automation AG or its affiliates.
 *
 *  All trademarks used are the property of their respective owners.
 *
 *  TTTech Industrial Automation AG and its affiliates do not assume any liability
 *  arising out of the application or use of any product described or shown
 *  herein. TTTech Industrial Automation AG and its affiliates reserve the right to
 *  make changes, at any time, in order to improve reliability, function or
 *  design.
 *
 *  Contact Information:
 *  support@tttech-industrial.com
 *
 *  TTTech Industrial Automation AG, Schoenbrunnerstrasse 7, 1040 Vienna, Austria
 *
 */

import { defineStore } from 'pinia';
import axios from 'axios';
import { DnaApiService } from '@/services/api';
import DnaModel from '@/model/dna/dna.model';
import useNodesStore from '@/store/modules/pinia/nodes';
import { downloadFileInBrowser } from '@/helpers/file-download';

const useDnaStore = defineStore('dna', {
  state: () => ({
    targetConfiguration: new DnaModel({}),
    nodeTargetConfiguration: new DnaModel({}),
  }),

  actions: {
    async fetchTargetConfiguration(serialNumber) {
      try {
        const nodesStore = useNodesStore();
        const source = axios.CancelToken.source();
        nodesStore.setCancelTokenDnaStatus(source);

        this.targetConfiguration = new DnaModel(
          await DnaApiService.getWorkloadTargetConfiguration({
            serialNumber,
            token: source.token,
          }),
        );
      } catch {
        this.targetConfiguration = new DnaModel({});
      }
    },
    async fetchTargetConfigurationEmpty() {
      this.targetConfiguration = new DnaModel({});
    },
    async fetchNodeTargetConfigurationEmpty() {
      this.nodeTargetConfiguration = new DnaModel({});
    },
    async fetchNodeTargetConfiguration(serialNumber) {
      try {
        const nodesStore = useNodesStore();
        const source = axios.CancelToken.source();
        nodesStore.setCancelTokenNodeDnaStatus(source);

        this.nodeTargetConfiguration = new DnaModel(
          await DnaApiService.getNodeTargetConfiguration({ serialNumber, token: source.token }),
        );
      } catch {
        this.nodeTargetConfiguration = new DnaModel({});
      }
    },
    async downloadCurrentConfiguration(serialNumber, typeOfDna) {
      const { data, filename } =
        typeOfDna === 'node'
          ? await DnaApiService.downloadNodeCurrentConfiguration(serialNumber)
          : await DnaApiService.downloadWorkloadCurrentConfiguration(serialNumber);
      downloadFileInBrowser(data, filename);
    },

    async downloadTargetConfiguration(serialNumber, typeOfDna) {
      const { data, filename } =
        typeOfDna === 'node'
          ? await DnaApiService.downloadNodeTargetConfiguration(serialNumber)
          : await DnaApiService.downloadWorkloadTargetConfiguration(serialNumber);
      downloadFileInBrowser(data, filename);
    },

    async reApplyTargetConfiguration(payload, typeOfDna) {
      typeOfDna === 'node'
        ? await DnaApiService.reApplyNodeTargetConfiguration(payload)
        : await DnaApiService.reApplyWorkloadTargetConfiguration(payload);
    },

    async applyConfiguration(payload, typeOfDna) {
      typeOfDna === 'node'
        ? await DnaApiService.applyNodeConfiguration(payload)
        : await DnaApiService.applyWorkloadConfiguration(payload);
    },

    async cancelApplyConfiguration(serialNumber) {
      await DnaApiService.cancelApplyWorkloadConfiguration(serialNumber);
    },

    mqttDna(payload) {
      const { type, ...dnaConfig } = payload;
      if (type === 'node') {
        this.nodeTargetConfiguration = new DnaModel(dnaConfig);
        return;
      }
      this.targetConfiguration = new DnaModel(dnaConfig);
    },
  },
});

export default useDnaStore;
